<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "profile"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "profile"

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const open = ref(false)

const variantList = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeIdx = ref(0)

const variants = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return []
  return variantList.value[activeIdx.value].values
})

const active = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return []
  return variantList.value[activeIdx.value]
})

const matchedIdx = computed(() => {
  if (!variantList.value.length || variantList.value.length < activeIdx.value) return -1
  return variantList.value[activeIdx.value].values.findIndex((v: any) => v.match)
})

const currentProfileText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

/**
 * image
 */

const hoverProfileIdx = ref(-1)

const setHoverProfileIdx = () => {
  if (matchedIdx.value !== -1 && variants.value.length >= matchedIdx.value) {
    hoverProfileIdx.value = matchedIdx.value
  }
}

watch(active, () => {
  setHoverProfileIdx()
})

watch(props.item, () => {
  setHoverProfileIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(variants.value, hoverProfileIdx.value))

const getProfileImageUrl = (profile: string, color: string) => {
  if (!profile || !color) return ''
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/cross_sections/' + profile.toLowerCase() + '/' + profile.toLowerCase() + '_30_20_' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const profiles = variantList.value;

  if (!profiles.length) return;
  const allListsCreated = profiles.every((profile: any) => profile.values && profile.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = profiles.flatMap((disc: any) => disc.values.map((profile: any) => getProfileImageUrl(profile.idx, mainMetal.value)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(variantList, () => {
  checkAllLoaded();
  if (variantList.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getProfileTipTitle = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.profiles.' + key + '.title')
  } else {
    return ''
  }
})

const getProfileTipText = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.profiles.' + key + '.text')
  } else {
    return ''
  }
})

const getProfileTipImageUrl = computed(() => {
  if (active.value && active.value.values[hoverProfileIdx.value]) {
    const key = active.value.values[hoverProfileIdx.value].idx.toLowerCase()
    return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/profile/600x600/" + key + "_30_20_yellow.webp"
  } else {
    return ''
  }
})

onMounted(() => {
      setHoverProfileIdx()
    }
)

/*
 * focus values
 */

const focusValueIdx = [
  "RK", "RL", "RM", "RP", "RT"
]

const focusValueIdxCount = 5

const getFocusValueIdx = computed(
    () => {
      let focusList = focusValueIdx

      if (matchedIdx.value) {
        if (!focusValueIdx.includes(variantList.value[activeIdx.value].values[matchedIdx.value].idx)) {
          focusList = [
            variantList.value[activeIdx.value].values[matchedIdx.value].idx,
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      const availableFocus = variants.value
          .map((v: any) => v.idx.toString())
          .filter((idx: string) => focusList.includes(idx))

      if (availableFocus.length < focusValueIdxCount) {
        const nextNonFocus = variants.value
            .map((v: any) => v.idx.toString())
            .filter((idx: string) => !availableFocus.includes(idx))

        availableFocus.push(...nextNonFocus.slice(0, focusValueIdxCount - availableFocus.length))
      }

      return availableFocus
    }
)


</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Profile"
      :subtitle="currentProfileText || 'Wählen Sie die Profilform für Ihre Trauringe'"
      :loading=loading
      :tip-title="getProfileTipTitle"
      :tip-text="getProfileTipText"
      :tip-image="getProfileTipImageUrl"
  >
    <template v-slot:main>

      <!-- most selected -->
      <item-v3-most-selected/>
      <!-- List -->
      <ul class="flex gap-x-1 flex-wrap mb-2 w-full justify-center ">
        <!-- disc list -->
        <template v-for="(profile, profileIdx) in variants">
          <li :key="profileIdx"
              v-if="getFocusValueIdx.includes(profile.idx)"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, profile)"
              @mouseover="hoverProfileIdx = profileIdx as number"
              @mouseleave="hoverProfileIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': profile.match,
              'border-2 border-transparent': !profile.match
            }"
          >
            <div
                class="w-12 h-12 rounded-2xl flex justify-center items-start overflow-hidden">
              <img
                  :src="getProfileImageUrl(profile.idx, mainMetal)"
                  :srcset="getProfileImageUrl(profile.idx, mainMetal)"
                  class="relative h-60 -top-22 object-cover"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ profile.idx }}
          </span>
          </li>
        </template>
      </ul>
    </template>
    <template v-slot:more>

      <ul class="flex gap-x-1 flex-wrap w-full justify-center ">
        <!-- disc list -->
        <template v-for="(profile, profileIdx) in variants">
          <li :key="profileIdx"
              v-if="!getFocusValueIdx.includes(profile.idx)"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, profile)"
              @mouseover="hoverProfileIdx = profileIdx as number"
              @mouseleave="hoverProfileIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': profile.match,
              'border-2 border-transparent': !profile.match
            }"
          >
            <div
                class="w-12 h-12 rounded-2xl flex justify-center items-start overflow-hidden">
              <img
                  :src="getProfileImageUrl(profile.idx, mainMetal)"
                  :srcset="getProfileImageUrl(profile.idx, mainMetal)"
                  class="relative h-60 -top-22 object-cover"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ profile.idx }}
          </span>
          </li>
        </template>
      </ul>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>

</style>